/**
 * Journal analytics. Pure functions over reconstructed trades.
 *
 * Everything here is derived from the user's own trades and reported as fact.
 * Nothing in this module produces a recommendation.
 */
import { dec, Decimal, sum, ZERO } from '../money.js';
import { tradingDayFor } from '../instruments/sessions.js';
import { sessionTemplateIdFor } from '../instruments/registry.js';
import { sessionTemplate } from '../instruments/sessions.js';
import type { Trade } from './types.js';

export interface Summary {
  readonly tradeCount: number;
  readonly winCount: number;
  readonly lossCount: number;
  readonly scratchCount: number;
  /** 0..1. Zero when there are no trades — never NaN. */
  readonly winRate: Decimal;
  readonly grossPnl: Decimal;
  readonly commissions: Decimal;
  readonly fees: Decimal;
  readonly netPnl: Decimal;
  readonly avgWin: Decimal;
  readonly avgLoss: Decimal;
  /** Average net P&L per trade. */
  readonly expectancy: Decimal;
  /** Gross wins / gross losses. Null when there are no losses. */
  readonly profitFactor: Decimal | null;
  readonly largestWin: Decimal;
  readonly largestLoss: Decimal;
  /** Average R across trades that have a risk basis. Null when none do. */
  readonly avgRMultiple: Decimal | null;
  readonly maxWinStreak: number;
  readonly maxLossStreak: number;
}

/**
 * What counts as breakeven.
 *
 * Exact zero is the wrong default for a real journal. A trader who scratches at
 * entry still pays commission, so a "flat" trade lands a few dollars negative
 * and every statistic treats it as a loss — win rate drops, loss streaks
 * lengthen, and the numbers stop matching how the trader remembers the session.
 *
 * The band is a preference rather than a constant because the right value is
 * per-trader and per-instrument: a few dollars on a micro contract is not the
 * same trade as a few dollars on a full-size one.
 *
 * Zero band restores the strict behaviour, which is what a tax export wants.
 */
export interface BreakevenOptions {
  /** Absolute net P&L within this of zero is a scratch. Never negative. */
  readonly bandUsd?: Decimal;
}

export const NO_BAND: BreakevenOptions = {};

export const isWin = (t: Trade, o: BreakevenOptions = NO_BAND): boolean =>
  o.bandUsd && !o.bandUsd.isZero() ? t.netPnl.gt(o.bandUsd.abs()) : t.netPnl.gt(0);

export const isLoss = (t: Trade, o: BreakevenOptions = NO_BAND): boolean =>
  o.bandUsd && !o.bandUsd.isZero() ? t.netPnl.lt(o.bandUsd.abs().negated()) : t.netPnl.lt(0);

/**
 * Win, loss or scratch — the single place that decides.
 *
 * Exported and used by every report, score and grouping rather than each one
 * re-deriving it from `netPnl`. A band that applied to the analytics panel but
 * not the reports screen would produce two screens that disagree about the same
 * trade, which is worse than having no band at all.
 */
export const outcomeOf = (t: Trade, o: BreakevenOptions = NO_BAND): 'win' | 'loss' | 'scratch' =>
  isWin(t, o) ? 'win' : isLoss(t, o) ? 'loss' : 'scratch';

export function summarise(trades: readonly Trade[], be: BreakevenOptions = NO_BAND): Summary {
  const closed = trades.filter((t) => !t.isOpen);
  const wins = closed.filter((t) => isWin(t, be));
  const losses = closed.filter((t) => isLoss(t, be));
  const n = closed.length;

  const grossWins = sum(wins.map((t) => t.netPnl));
  const grossLosses = sum(losses.map((t) => t.netPnl)).abs();
  const netPnl = sum(closed.map((t) => t.netPnl));

  let maxWinStreak = 0, maxLossStreak = 0, curWin = 0, curLoss = 0;
  for (const t of closed) {
    if (isWin(t, be)) { curWin++; curLoss = 0; }
    else if (isLoss(t, be)) { curLoss++; curWin = 0; }
    else { curWin = 0; curLoss = 0; }
    if (curWin > maxWinStreak) maxWinStreak = curWin;
    if (curLoss > maxLossStreak) maxLossStreak = curLoss;
  }

  const withR = closed.filter((t) => t.rMultiple !== null);

  return {
    tradeCount: n,
    winCount: wins.length,
    lossCount: losses.length,
    scratchCount: n - wins.length - losses.length,
    winRate: n === 0 ? ZERO : dec(wins.length).div(n),
    grossPnl: sum(closed.map((t) => t.grossPnl)),
    commissions: sum(closed.map((t) => t.commissions)),
    fees: sum(closed.map((t) => t.fees)),
    netPnl,
    avgWin: wins.length === 0 ? ZERO : grossWins.div(wins.length),
    avgLoss: losses.length === 0 ? ZERO : grossLosses.div(losses.length).negated(),
    expectancy: n === 0 ? ZERO : netPnl.div(n),
    profitFactor: grossLosses.isZero() ? null : grossWins.div(grossLosses),
    largestWin: wins.length === 0 ? ZERO : Decimal.max(...wins.map((t) => t.netPnl)),
    largestLoss: losses.length === 0 ? ZERO : Decimal.min(...losses.map((t) => t.netPnl)),
    avgRMultiple: withR.length === 0
      ? null
      : sum(withR.map((t) => t.rMultiple!)).div(withR.length),
    maxWinStreak,
    maxLossStreak,
  };
}

export interface EquityPoint {
  readonly at: Date;
  readonly equity: Decimal;
  readonly drawdown: Decimal;
}

/** Cumulative net P&L with running drawdown from the high-water mark. */
export function equityCurve(trades: readonly Trade[], startingBalance: Decimal = ZERO): readonly EquityPoint[] {
  const closed = trades
    .filter((t) => !t.isOpen && t.closedAt)
    .sort((a, b) => a.closedAt!.getTime() - b.closedAt!.getTime());

  const out: EquityPoint[] = [];
  let equity = startingBalance;
  let peak = startingBalance;
  for (const t of closed) {
    equity = equity.plus(t.netPnl);
    if (equity.gt(peak)) peak = equity;
    out.push({ at: t.closedAt!, equity, drawdown: equity.minus(peak) });
  }
  return out;
}

/** Worst peak-to-trough decline. Returned as a negative number, or zero. */
export function maxDrawdown(trades: readonly Trade[], startingBalance: Decimal = ZERO): Decimal {
  const curve = equityCurve(trades, startingBalance);
  return curve.reduce<Decimal>((w, p) => (p.drawdown.lt(w) ? p.drawdown : w), ZERO);
}

export interface Breakdown {
  readonly key: string;
  readonly tradeCount: number;
  readonly netPnl: Decimal;
  readonly winRate: Decimal;
}

function group(
  trades: readonly Trade[], keyOf: (t: Trade) => string, be: BreakevenOptions = NO_BAND,
): readonly Breakdown[] {
  const buckets = new Map<string, Trade[]>();
  for (const t of trades.filter((x) => !x.isOpen)) {
    const k = keyOf(t);
    const b = buckets.get(k);
    if (b) b.push(t); else buckets.set(k, [t]);
  }
  return [...buckets.entries()]
    .map(([key, ts]) => {
      const s = summarise(ts, be);
      return { key, tradeCount: s.tradeCount, netPnl: s.netPnl, winRate: s.winRate };
    })
    .sort((a, b) => b.netPnl.comparedTo(a.netPnl));
}

export const byInstrument = (t: readonly Trade[], be: BreakevenOptions = NO_BAND): readonly Breakdown[] =>
  group(t, (x) => x.symbol, be);

/** Buckets by hour in the instrument's exchange timezone, not the user's. */
export const byHourOfDay = (t: readonly Trade[], be: BreakevenOptions = NO_BAND): readonly Breakdown[] =>
  group(t, (x) => {
    const tpl = sessionTemplate(sessionTemplateIdFor(x.symbol));
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tpl.timezone, hour: '2-digit', hour12: false,
    });
    return `${fmt.format(x.openedAt)}:00`;
  }, be);

export const byDayOfWeek = (t: readonly Trade[], be: BreakevenOptions = NO_BAND): readonly Breakdown[] =>
  group(t, (x) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][x.openedAt.getUTCDay()] ?? '?', be);

/** Buckets by CME trading day, which is not the calendar day. */
export const byTradingDay = (t: readonly Trade[], be: BreakevenOptions = NO_BAND): readonly Breakdown[] =>
  group(t, (x) => tradingDayFor(sessionTemplate(sessionTemplateIdFor(x.symbol)), x.openedAt), be);

export const byDirection = (t: readonly Trade[], be: BreakevenOptions = NO_BAND): readonly Breakdown[] =>
  group(t, (x) => x.direction, be);

/** Net P&L per trading day, used by the daily-loss rule engines. */
export function dailyPnl(trades: readonly Trade[]): ReadonlyMap<string, Decimal> {
  const out = new Map<string, Decimal>();
  for (const t of trades) {
    if (t.isOpen || !t.closedAt) continue;
    const tpl = sessionTemplate(sessionTemplateIdFor(t.symbol));
    const day = tradingDayFor(tpl, t.closedAt);
    out.set(day, (out.get(day) ?? ZERO).plus(t.netPnl));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Deep statistics — the figures a trader goes looking for by name.
//
// `Summary` holds the sixteen figures every screen needs. This block holds the
// rest: day-level and month-level aggregates, and hold times.
//
// Why it is a separate function rather than more fields on `Summary`: these are
// materially more expensive (they group, sort and re-summarise) and almost every
// caller of `summarise` wants none of them. A KPI strip that recomputes monthly
// aggregates on every render is a real cost for a figure nobody is looking at.
// ---------------------------------------------------------------------------

/** A trading day as the day-level statistics count it. */
export interface DayStat {
  /** Trading day (`YYYY-MM-DD`), from the instrument's session template. */
  readonly day: string;
  readonly netPnl: Decimal;
  readonly tradeCount: number;
  /**
   * Contracts traded that day — the sum of each trade's peak position size.
   *
   * Peak size rather than fill count, because a trade scaled in over six fills
   * and out over four is one position of N contracts, not ten of anything. It
   * is the figure a trader recognises as "how much size did I do".
   */
  readonly volume: Decimal;
}

export interface MonthStat {
  /** `YYYY-MM`, derived from the trading day so it agrees with the calendar. */
  readonly month: string;
  readonly netPnl: Decimal;
  readonly tradeCount: number;
}

export interface DeepSummary {
  // --- days ---
  readonly tradingDays: number;
  readonly winningDays: number;
  readonly losingDays: number;
  readonly breakevenDays: number;
  readonly maxWinningDayStreak: number;
  readonly maxLosingDayStreak: number;
  /** Mean over days that have trades — not over calendar days. */
  readonly avgDailyPnl: Decimal;
  readonly avgWinningDayPnl: Decimal;
  readonly avgLosingDayPnl: Decimal;
  readonly largestWinningDay: Decimal;
  readonly largestLosingDay: Decimal;
  readonly avgDailyVolume: Decimal;
  readonly totalVolume: Decimal;
  // --- months ---
  readonly monthsTraded: number;
  readonly bestMonth: MonthStat | null;
  readonly worstMonth: MonthStat | null;
  readonly avgMonthlyPnl: Decimal;
  // --- hold time, in seconds ---
  //
  // Null rather than zero when nothing qualifies. A journal with no losers has
  // no average losing hold time, and printing `0s` would assert that it does.
  readonly avgHoldSeconds: number | null;
  readonly avgHoldSecondsWinners: number | null;
  readonly avgHoldSecondsLosers: number | null;
  readonly avgHoldSecondsScratches: number | null;
  // --- open ---
  readonly openTradeCount: number;
}

/** The trading day a trade lands on: the day its P&L was realised. */
const dayOf = (t: Trade, at: Date): string =>
  tradingDayFor(sessionTemplate(sessionTemplateIdFor(t.symbol)), at);

/**
 * Per-day aggregates, ascending by day.
 *
 * Keyed on `closedAt`, matching `dailyPnl` and for the same reason: a daily-loss
 * limit is breached when the loss is *realised*, not when the position was
 * opened. A trade held overnight belongs to the day it was closed.
 */
export function dayStats(trades: readonly Trade[]): readonly DayStat[] {
  const acc = new Map<string, { netPnl: Decimal; tradeCount: number; volume: Decimal }>();
  for (const t of trades) {
    if (t.isOpen || !t.closedAt) continue;
    const day = dayOf(t, t.closedAt);
    const cur = acc.get(day) ?? { netPnl: ZERO, tradeCount: 0, volume: ZERO };
    acc.set(day, {
      netPnl: cur.netPnl.plus(t.netPnl),
      tradeCount: cur.tradeCount + 1,
      volume: cur.volume.plus(t.quantity.abs()),
    });
  }
  return [...acc.entries()]
    .map(([day, v]) => ({ day, ...v }))
    .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
}

/** Per-month aggregates, ascending. Months with no trades are absent, not zero. */
export function monthStats(trades: readonly Trade[]): readonly MonthStat[] {
  const acc = new Map<string, { netPnl: Decimal; tradeCount: number }>();
  for (const d of dayStats(trades)) {
    const month = d.day.slice(0, 7);
    const cur = acc.get(month) ?? { netPnl: ZERO, tradeCount: 0 };
    acc.set(month, {
      netPnl: cur.netPnl.plus(d.netPnl),
      tradeCount: cur.tradeCount + d.tradeCount,
    });
  }
  return [...acc.entries()]
    .map(([month, v]) => ({ month, ...v }))
    .sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
}

const meanSeconds = (ts: readonly Trade[]): number | null => {
  const held = ts.filter((t) => t.closedAt !== null);
  if (held.length === 0) return null;
  const total = held.reduce(
    (n, t) => n + ((t.closedAt as Date).getTime() - t.openedAt.getTime()),
    0,
  );
  return Math.round(total / held.length / 1000);
};

const mean = (xs: readonly Decimal[]): Decimal =>
  xs.length === 0 ? ZERO : sum(xs).dividedBy(xs.length);

export function deepSummarise(
  trades: readonly Trade[], be: BreakevenOptions = NO_BAND,
): DeepSummary {
  const closed = trades.filter((t) => !t.isOpen);
  const days = dayStats(trades);
  const months = monthStats(trades);

  // A day is won or lost by the same band that decides a trade. Two different
  // definitions of "green" between the trade table and the day table is the
  // kind of disagreement a trader notices and never trusts again.
  const band = be.bandUsd?.abs() ?? ZERO;
  const dayWon = (d: DayStat): boolean => d.netPnl.gt(band);
  const dayLost = (d: DayStat): boolean => d.netPnl.lt(band.negated());

  let maxWin = 0, maxLoss = 0, curWin = 0, curLoss = 0;
  for (const d of days) {
    if (dayWon(d)) { curWin += 1; curLoss = 0; }
    else if (dayLost(d)) { curLoss += 1; curWin = 0; }
    else { curWin = 0; curLoss = 0; }
    if (curWin > maxWin) maxWin = curWin;
    if (curLoss > maxLoss) maxLoss = curLoss;
  }

  const winDays = days.filter(dayWon);
  const lossDays = days.filter(dayLost);
  const volumes = days.map((d) => d.volume);

  return {
    tradingDays: days.length,
    winningDays: winDays.length,
    losingDays: lossDays.length,
    breakevenDays: days.length - winDays.length - lossDays.length,
    maxWinningDayStreak: maxWin,
    maxLosingDayStreak: maxLoss,
    avgDailyPnl: mean(days.map((d) => d.netPnl)),
    avgWinningDayPnl: mean(winDays.map((d) => d.netPnl)),
    avgLosingDayPnl: mean(lossDays.map((d) => d.netPnl)),
    largestWinningDay: days.reduce<Decimal>((m, d) => (d.netPnl.gt(m) ? d.netPnl : m), ZERO),
    largestLosingDay: days.reduce<Decimal>((m, d) => (d.netPnl.lt(m) ? d.netPnl : m), ZERO),
    avgDailyVolume: mean(volumes),
    totalVolume: sum(volumes),

    monthsTraded: months.length,
    bestMonth: months.reduce<MonthStat | null>(
      (m, x) => (m === null || x.netPnl.gt(m.netPnl) ? x : m), null,
    ),
    worstMonth: months.reduce<MonthStat | null>(
      (m, x) => (m === null || x.netPnl.lt(m.netPnl) ? x : m), null,
    ),
    avgMonthlyPnl: mean(months.map((m) => m.netPnl)),

    avgHoldSeconds: meanSeconds(closed),
    avgHoldSecondsWinners: meanSeconds(closed.filter((t) => isWin(t, be))),
    avgHoldSecondsLosers: meanSeconds(closed.filter((t) => isLoss(t, be))),
    avgHoldSecondsScratches: meanSeconds(
      closed.filter((t) => outcomeOf(t, be) === 'scratch'),
    ),

    openTradeCount: trades.length - closed.length,
  };
}
